import { AxiosResponse } from "axios";
import apiService from "services/apiService";
import {
  ApproveFeedbackRequest,
  DEFAULT_PAGE_SIZE,
  Feedback,
  FeedbackSummaryPage,
  FeedbackSummaryQuery,
  KraArea,
  KraObjective,
  KraWriteRequest,
  ManagerFeedbackRequest,
  SelfFeedbackRequest,
  UserFeedbackQuery,
  UserFeedbackResponse,
} from "./feedbackInterface";

/**
 * Builds a query string from a flat params object, dropping undefined, null and
 * blank values and trimming strings.
 *
 * NOTE: shared by the read calls so no endpoint can accidentally send
 * `?search=` or `?year=undefined`, which the server rejects with a 400.
 */
const toQueryString = (params: Record<string, string | number | undefined | null>): string => {
  const query = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    const text = typeof value === "string" ? value.trim() : String(value);
    if (text === "") return;
    query.set(key, text);
  });

  return query.toString();
};

/**
 * Quarterly Feedback API client.
 *
 * NOTE: follows the existing `*Manager` convention (see
 * `services/Reimbursement/reimbursementManager.ts`) — one class holding the
 * base path, every call going through the shared `apiService` Axios instance so
 * the Bearer token, tenant host and 401 handling stay centralised.
 */
class FeedbackManager {
  private url: string;

  constructor() {
    this.url = "api/feedback";
  }

  // ── KRA admin config ──────────────────────────────────────────────────────

  /**
   * `includeInactive=false` (the default) returns only active areas with their
   * active objectives, which is what the goal-sheet dropdowns need. The admin
   * config screen passes `true` so deactivated rows stay manageable.
   */
  async getKraAreas(includeInactive = false): Promise<AxiosResponse<KraArea[]>> {
    return apiService.get(`${this.url}/kra-areas?includeInactive=${includeInactive}`);
  }

  async createKraArea(payload: KraWriteRequest): Promise<AxiosResponse<KraArea>> {
    return apiService.post(`${this.url}/kra-areas`, payload);
  }

  async updateKraArea(id: number, payload: KraWriteRequest): Promise<AxiosResponse<KraArea>> {
    return apiService.put(`${this.url}/kra-areas/${id}`, payload);
  }

  /** Refused (400) for defaults, areas with objectives, or areas in use. */
  async deleteKraArea(id: number): Promise<AxiosResponse<string>> {
    return apiService.delete(`${this.url}/kra-areas/${id}`);
  }

  async createKraObjective(
    areaId: number,
    payload: KraWriteRequest,
  ): Promise<AxiosResponse<KraObjective>> {
    return apiService.post(`${this.url}/kra-areas/${areaId}/objectives`, payload);
  }

  async updateKraObjective(
    id: number,
    payload: KraWriteRequest,
  ): Promise<AxiosResponse<KraObjective>> {
    return apiService.put(`${this.url}/kra-objectives/${id}`, payload);
  }

  async deleteKraObjective(id: number): Promise<AxiosResponse<string>> {
    return apiService.delete(`${this.url}/kra-objectives/${id}`);
  }

  // ── Self feedback ─────────────────────────────────────────────────────────

  /** Always about the caller; `submit: true` validates and locks the sheet. */
  async createSelfFeedback(payload: SelfFeedbackRequest): Promise<AxiosResponse<Feedback>> {
    return apiService.post(`${this.url}/self`, payload);
  }

  /** DRAFT only. Replaces comment, rating and the whole row list. */
  async updateSelfFeedback(
    id: number,
    payload: SelfFeedbackRequest,
  ): Promise<AxiosResponse<Feedback>> {
    return apiService.put(`${this.url}/self/${id}`, payload);
  }

  /** DRAFT only. Returns a plain-text body, not JSON. */
  async deleteSelfFeedback(id: number): Promise<AxiosResponse<string>> {
    return apiService.delete(`${this.url}/self/${id}`);
  }

  // ── Manager feedback ──────────────────────────────────────────────────────

  /**
   * Standalone feedback about an employee. Send exactly one of `userId` or
   * `email`; `overallComment` is required.
   */
  async createManagerFeedback(
    payload: ManagerFeedbackRequest,
  ): Promise<AxiosResponse<Feedback>> {
    return apiService.post(`${this.url}/employee`, payload);
  }

  /**
   * Approving a submitted sheet.
   *
   * NOTE: approval is NOT a separate endpoint — it is the same POST /employee
   * carrying `selfFeedbackId`, which saves the reviewer's feedback and flips the
   * sheet to APPROVED in one call.
   *
   * CHANGE: goal rows are now allowed on an approving record, so `items` is
   * forwarded instead of being dropped. The employee and quarter still come
   * from the sheet, so `userId`, `email`, `year` and `quarter` are deliberately
   * never sent — the server rejects any that disagree with the sheet.
   */
  async approveSelfFeedback(
    payload: ApproveFeedbackRequest,
  ): Promise<AxiosResponse<Feedback>> {
    const {
      selfFeedbackId,
      overallComment,
      overallRating = null,
      items,
      visibleToEmployee,
    } = payload;

    const body: ManagerFeedbackRequest = { selfFeedbackId, overallComment, overallRating };

    // Only sent when the reviewer actually scored the goals; an empty array
    // would wipe rows the server would otherwise leave alone.
    if (items && items.length > 0) body.items = items;
    if (visibleToEmployee !== undefined) body.visibleToEmployee = visibleToEmployee;

    return apiService.post(`${this.url}/employee`, body);
  }

  /** Author or admin. `userId`, `email` and `selfFeedbackId` are ignored here. */
  async updateManagerFeedback(
    id: number,
    payload: ManagerFeedbackRequest,
  ): Promise<AxiosResponse<Feedback>> {
    return apiService.put(`${this.url}/employee/${id}`, payload);
  }

  /** Refused for the feedback that approved a sheet — edit it instead. */
  async deleteManagerFeedback(id: number): Promise<AxiosResponse<string>> {
    return apiService.delete(`${this.url}/employee/${id}`);
  }

  // ── Reading ───────────────────────────────────────────────────────────────

  /** One feedback by id — used when opening from a notification. */
  async getFeedback(id: number): Promise<AxiosResponse<Feedback>> {
    return apiService.get(`${this.url}/${id}`);
  }

  /**
   * One employee's screen for one year.
   *
   * Omit `userId`/`email` for yourself. Omitting `kind` fills both lists
   * (Overview); passing one fills only that tab's list, which keeps the
   * per-tab payload small.
   */
  async getUserFeedback(
    params: UserFeedbackQuery = {},
  ): Promise<AxiosResponse<UserFeedbackResponse>> {
    const qs = toQueryString({ ...params });
    return apiService.get(`${this.url}/user${qs ? `?${qs}` : ""}`);
  }

  /** The manager/admin list screen: one row per active employee. */
  async getSummary(
    params: FeedbackSummaryQuery = {},
  ): Promise<AxiosResponse<FeedbackSummaryPage>> {
    // page and size are always sent, so a blank search can never shift them.
    const qs = toQueryString({
      ...params,
      page: params.page ?? 0,
      size: params.size ?? DEFAULT_PAGE_SIZE,
    });
    return apiService.get(`${this.url}/summary?${qs}`);
  }
}

const feedbackManager = new FeedbackManager();
export default feedbackManager;
